import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import './Home.css';
import './GallerySection.css';
import Footer from '../Components/Footer';

/* =========================================================
   Custom hook: scroll-triggered reveal
   ========================================================= */
const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (!options.repeat) observer.unobserve(element);
        } else if (options.repeat) {
          setIsVisible(false);
        }
      },
      { threshold: options.threshold || 0.15, rootMargin: options.rootMargin || '0px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [options.repeat, options.threshold, options.rootMargin]);

  return [ref, isVisible];
};

/* =========================================================
   AnimatedCounter
   ========================================================= */
const AnimatedCounter = ({ value, suffix = '', duration = 1500 }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useScrollReveal({ threshold: 0.5 });

  useEffect(() => {
    if (!isVisible) return;

    const numericValue = parseInt(String(value).replace(/\D/g, ''), 10);
    if (isNaN(numericValue)) {
      setCount(value);
      return;
    }

    let start = 0;
    const increment = numericValue / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= numericValue) {
        setCount(numericValue);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [isVisible, value, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

/* =========================================================
   Giant background typography
   ========================================================= */
const BackgroundType = ({ text = 'AVTECH' }) => (
  <div className="bg-giant-type" aria-hidden="true">
    <span>{text}</span>
  </div>
);

/* =========================================================
   Fixed editorial side rails
   ========================================================= */
const SideRails = ({ clock }) => (
  <>
    <div className="editorial-rail editorial-rail-left">
      <span className="rail-text">AVTECH · EST. 2024</span>
      <span className="rail-line" />
      <span className="rail-text">
        {clock.toLocaleTimeString('en-US', { hour12: false })}
      </span>
    </div>
    <div className="editorial-rail editorial-rail-right">
      <span className="rail-text">SCROLL</span>
      <span className="rail-line" />
      <span className="rail-text">↓</span>
    </div>
  </>
);

/* =========================================================
   Home — main page
   ========================================================= */
export default function Home() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [galleryError, setGalleryError] = useState('');
  const [scrollDirection, setScrollDirection] = useState('down');
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [clock, setClock] = useState(new Date());

  const SERVER_URL = 'https://api.avtechbmt.cloud';
  const scrollContainerRef = useRef(null);
  const lastScrollY = useRef(0);

  // Reveal hooks
  const [servicesRef, servicesVisible] = useScrollReveal({ threshold: 0.2 });
  const [genesisRef, genesisVisible] = useScrollReveal({ threshold: 0.2 });
  const [productsRef, productsVisible] = useScrollReveal({ threshold: 0.2 });
  const [academyRef, academyVisible] = useScrollReveal({ threshold: 0.2 });
  const [contactRef, contactVisible] = useScrollReveal({ threshold: 0.2 });
  const [philosophyRef, philosophyVisible] = useScrollReveal({ threshold: 0.2 });
  const [galleryRef, galleryVisible] = useScrollReveal({ threshold: 0.05 });

  /* ---------------------------------------------------------
     Robust image URL builder
     --------------------------------------------------------- */
  const getImageUrl = useCallback((path) => {
    if (!path || typeof path !== 'string') return '';

    const trimmed = path.trim();

    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (/^\/\//.test(trimmed)) return `https:${trimmed}`;

    let cleanPath = trimmed.replace(/\\/g, '/').replace(/^\/+/, '');
    return `${SERVER_URL}/${cleanPath}`;
  }, []);

  const handleImageError = useCallback((id, fullUrl) => {
    console.error(`[Gallery] Image failed to load:`, fullUrl);
    setImageErrors((prev) => ({ ...prev, [id]: true }));
  }, []);

  /* ---------------------------------------------------------
     Fetch public gallery — all active items (capped at render)
     --------------------------------------------------------- */
  useEffect(() => {
    document.body.classList.add('home-page');
    let cancelled = false;

    const fetchGallery = async () => {
      setGalleryError('');
      setLoadingGallery(true);
      try {
        const url = `${SERVER_URL}/api/v1/gallery?limit=100&isActive=true`;
        const response = await fetch(url);

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error(`Server returned ${response.status} (non-JSON response)`);
        }

        const result = await response.json();
        if (cancelled) return;

        if (!response.ok) {
          throw new Error(result.message || `Failed to load gallery (${response.status})`);
        }

        const rawList = Array.isArray(result)
          ? result
          : Array.isArray(result.data)
          ? result.data
          : [];

        const activeList = rawList.filter((item) => {
          if (item.isActive === undefined) return true;
          if (typeof item.isActive === 'boolean') return item.isActive;
          if (typeof item.isActive === 'string') return item.isActive.toLowerCase() === 'true';
          return Boolean(item.isActive);
        });

        activeList.sort((a, b) => {
          const oa = Number(a.order) || 0;
          const ob = Number(b.order) || 0;
          if (oa !== ob) return oa - ob;
          const ca = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const cb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return cb - ca;
        });

        console.log(
          '[Gallery] API returned', rawList.length,
          'item(s); active after filter:', activeList.length
        );

        setGalleryItems(activeList);

        if (activeList.length === 0) {
          setGalleryError('No public gallery items available.');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load gallery photos', err);
        setGalleryError(err.message || 'Failed to load gallery');
        setGalleryItems([]);
      } finally {
        if (!cancelled) setLoadingGallery(false);
      }
    };

    fetchGallery();

    return () => {
      cancelled = true;
      document.body.classList.remove('home-page');
    };
  }, []);

  /* Scroll direction */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const currentScrollY = container.scrollTop;
      setScrollDirection(currentScrollY > lastScrollY.current ? 'down' : 'up');
      lastScrollY.current = currentScrollY;
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  /* Mouse parallax */
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  /* Live clock */
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  /* Esc key closes lightbox */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    if (selectedImage) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  /* ---------------------------------------------------------
     Show only the first 6 active items
     --------------------------------------------------------- */
  const visibleGalleryItems = useMemo(
    () => galleryItems.slice(0, 6),
    [galleryItems]
  );

  /* ---------------------------------------------------------
     Inline reveal style — bypasses CSS specificity conflicts
     --------------------------------------------------------- */
  const revealStyle = useCallback(
    (delaySeconds) => ({
      opacity: galleryVisible ? 1 : 0,
      transform: galleryVisible ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.7s cubic-bezier(0.2, 0.9, 0.3, 1) ${delaySeconds}s, transform 0.7s cubic-bezier(0.2, 0.9, 0.3, 1) ${delaySeconds}s`,
      willChange: 'opacity, transform',
    }),
    [galleryVisible]
  );

  return (
    <div className="scroll-container" ref={scrollContainerRef}>
      <div className="grain-overlay" aria-hidden="true" />
      <SideRails clock={clock} />

      {/* ==============================
          SECTION 1: HERO
         ============================== */}
      <section className="snap-section hero-section">
        <BackgroundType />
        <div
          className="hero-shape hero-shape-1"
          style={{ transform: `translate(${mousePos.x * 0.5}px, ${mousePos.y * 0.5}px)` }}
        />
        <div
          className="hero-shape hero-shape-2"
          style={{ transform: `translate(${mousePos.x * -0.3}px, ${mousePos.y * -0.3}px)` }}
        />
        <div className="hero-content">
          <div className="hero-grid">
            <div className="hero-main">
              <div className="hero-logo-badge animate-in" style={{ animationDelay: '0.05s' }}>
                <img src="/avtech-logo.jpeg" alt="AVTECH" className="hero-logo-img" />
                <span className="hero-logo-label">AVTECH · Est. 2024</span>
              </div>
              <div className="eyebrow animate-in" style={{ animationDelay: '0.1s' }}>
                Independent Tech Collective · Est. 2024
              </div>
              <h1 className="hero-title">
                <span className="title-line animate-in" style={{ animationDelay: '0.2s' }}>
                  We build. We deploy.
                </span>
                <br />
                <span className="title-line text-muted animate-in" style={{ animationDelay: '0.35s' }}>
                  We <em>educate.</em>
                </span>
              </h1>
              <p className="hero-subtitle animate-in" style={{ animationDelay: '0.5s' }}>
                A modern tech collective. We craft custom software on MERN, Next.js,
                and PHP · Laravel, license production-ready assets, and train
                tomorrow's engineers.
              </p>
              <div className="hero-actions animate-in" style={{ animationDelay: '0.65s' }}>
                <a href="#genesis" className="cta-ghost">
                  Read our story
                  <span className="ghost-arrow">→</span>
                </a>
              </div>
            </div>

            {/* Right column — index card + note */}
            <aside className="hero-side">
              <div className="hero-index-card animate-in" style={{ animationDelay: '0.75s' }}>
                <header className="index-head">
                  <span className="index-head-label">Practice</span>
                  <span className="index-head-meta">Est. 2024 — Present</span>
                </header>

                <ul className="index-list" role="list">
                  <li>
                    <a
                      href="/services?category=build&service=fullstack#core-services"
                      className="index-row"
                    >
                      <span className="index-num">01</span>
                      <span className="index-name">Product Engineering</span>
                      <span className="index-arrow" aria-hidden="true">→</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="/services?category=intelligence&service=ai#core-services"
                      className="index-row"
                    >
                      <span className="index-num">02</span>
                      <span className="index-name">Digital Licensing</span>
                      <span className="index-arrow" aria-hidden="true">→</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="/services?category=build&service=webapp#core-services"
                      className="index-row"
                    >
                      <span className="index-num">03</span>
                      <span className="index-name">Engineering Academy</span>
                      <span className="index-arrow" aria-hidden="true">→</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="/services?category=cloud&service=cloud-support#core-services"
                      className="index-row"
                    >
                      <span className="index-num">04</span>
                      <span className="index-name">Cloud &amp; Infrastructure</span>
                      <span className="index-arrow" aria-hidden="true">→</span>
                    </a>
                  </li>
                </ul>

                <footer className="index-foot">
                  <span className="index-foot-label">Working with</span>
                  <span className="index-foot-value">
                    Founders · Studios · Product Teams
                  </span>
                </footer>
              </div>

              <div className="hero-note animate-in" style={{ animationDelay: '0.9s' }}>
                <div className="note-tag">// Current signal</div>
                <p>
                  We believe great software should feel like a well-designed object —
                  quiet, dependable, and beautiful in its simplicity.
                </p>
                <div className="note-stats">
                  <div>
                    <span><AnimatedCounter value="12" suffix="+" /></span>
                    <small>products</small>
                  </div>
                  <div>
                    <span><AnimatedCounter value="100" suffix="%" /></span>
                    <small>delivery</small>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
        <div className="scroll-indicator">
          <span className="scroll-text">Scroll down</span>
          <div className="scroll-mouse">
            <div className="scroll-wheel" />
          </div>
        </div>
      </section>

      {/* ==============================
          SECTION 2: SERVICES
         ============================== */}
      <section id="services" className="snap-section services-section">
        <BackgroundType />
        <div className="section-inner" ref={servicesRef}>
          <div className={`section-header ${servicesVisible ? 'revealed' : ''}`}>
            <span className="tag reveal-item">01 / Engineering</span>
            <h2 className="reveal-item" style={{ transitionDelay: '0.1s' }}>
              Tailored <em>Solutions</em>
            </h2>
            <p className="section-intro reveal-item" style={{ transitionDelay: '0.2s' }}>
              From full-stack builds to AI integrations, cloud infrastructure, and
              security — we deliver the technical foundation your business runs on.
            </p>
          </div>
          <div className={`services-grid ${servicesVisible ? 'revealed' : ''}`}>
            {[
              { num: '01', title: 'Full-Stack Development', desc: 'Custom web applications engineered on MERN, Next.js, and PHP · Laravel — built for performance, scalability, and seamless user experiences from database to interface.' },
              { num: '02', title: 'Web & Mobile Applications', desc: 'Fast, scalable web and mobile apps — cross-platform React Native or native — built to feel instant on every device.' },
              { num: '03', title: 'AI Integrations & Automation', desc: 'Seamlessly integrate AI into existing applications to enhance functionality, improve efficiency, and drive innovation across workflows.' },
              { num: '04', title: 'DevOps & Cloud Deployment', desc: 'Automated CI/CD pipelines, containerized infrastructure, and scalable cloud hosting on AWS, GCP, or Vercel.' },
              { num: '05', title: 'Cybersecurity Lab', desc: 'Enterprise-grade security solutions, compliance audits, penetration testing, and 24/7 threat monitoring.' },
              { num: '06', title: 'IT Strategy & Consulting', desc: 'Transforming complex tech challenges into clear, actionable roadmaps tailored specifically to your business goals.' },
            ].map((service, idx) => (
              <article key={idx} className="service-card reveal-item" style={{ transitionDelay: `${0.15 + idx * 0.08}s` }}>
                <span className="service-card-num">{service.num}</span>
                <h3 className="service-card-title">{service.title}</h3>
                <p className="service-card-desc">{service.desc}</p>
              </article>
            ))}
          </div>

          {/* Stack ribbon — professional one-liner of technologies */}
          <div className={`services-stack-ribbon reveal-item ${servicesVisible ? 'revealed' : ''}`} style={{ transitionDelay: '0.55s' }}>
            <span className="stack-ribbon-label">Stack</span>
            <span className="stack-ribbon-divider" />
            <ul className="stack-ribbon-list">
              <li>MERN</li>
              <li>Next.js</li>
              <li>PHP</li>
              <li>Laravel</li>
              <li>React Native</li>
               <li>Flutter</li>
              <li>Node.js</li>
              <li>PostgreSQL</li>
              <li>AWS</li>
            </ul>
          </div>

          <div className={`services-cta-row reveal-item ${servicesVisible ? 'revealed' : ''}`} style={{ transitionDelay: '0.65s' }}>
            <a href="/services?category=build&service=fullstack#core-services" className="cta-primary">
              <span>View All Services</span>
              <svg className="btn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <span className="services-cta-note">
              14+ services across build, cloud, and intelligence
            </span>
          </div>
        </div>
      </section>

      {/* ==============================
          SECTION 3: GENESIS
         ============================== */}
      <section id="genesis" className="snap-section genesis-section">
        <BackgroundType />
        <div className="section-inner" ref={genesisRef}>
          <div className={`genesis-grid ${genesisVisible ? 'revealed' : ''}`}>
            <div className="genesis-left">
              <span className="tag reveal-item" style={{ transitionDelay: '0.1s' }}>02 / Genesis</span>
              <h2 className="reveal-item" style={{ transitionDelay: '0.2s' }}>
                Engineering the<br /><em>digital</em> frontier.
              </h2>
              <div className="genesis-divider reveal-item" style={{ transitionDelay: '0.3s' }} />
              <p className="genesis-lead reveal-item" style={{ transitionDelay: '0.4s' }}>
                We don't just write code — we architect ecosystems.
              </p>
              <div className="genesis-metrics reveal-item" style={{ transitionDelay: '0.5s' }}>
                <div className="metric">
                  <span className="metric-value"><AnimatedCounter value="2024" /></span>
                  <span className="metric-label">Founded</span>
                </div>
                <div className="metric">
                  <span className="metric-value"><AnimatedCounter value="12" suffix="+" /></span>
                  <span className="metric-label">Products</span>
                </div>
                <div className="metric">
                  <span className="metric-value"><AnimatedCounter value="100" suffix="%" /></span>
                  <span className="metric-label">Delivery</span>
                </div>
              </div>
            </div>
            <div className="genesis-right">
              <div className="genesis-card reveal-item" style={{ transitionDelay: '0.3s' }}>
                <blockquote>
                  <span className="quote-mark">"</span>
                  <p>
                    We founded AVTECH to bridge the gap between abstract code and real-world utility.
                    Instead of just consulting, we actively build our own ecosystem of digital goods
                    while training hungry minds to engineer the software of tomorrow.
                  </p>
                  <footer>
                    <span className="highlight">No bloat, just performance.</span>
                  </footer>
                </blockquote>
              </div>
              <ul className="genesis-pillars">
                {[
                  { title: 'Performance', desc: 'Sub-100ms response times' },
                  { title: 'Security', desc: 'Zero-trust architecture' },
                  { title: 'Scalability', desc: 'Built for exponential growth' },
                ].map((pillar, idx) => (
                  <li key={idx} className="pillar reveal-item" style={{ transitionDelay: `${0.4 + idx * 0.1}s` }}>
                    <span className="pillar-num">0{idx + 1}</span>
                    <div>
                      <h4>{pillar.title}</h4>
                      <p>{pillar.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          SECTION 4: ACADEMY
         ============================== */}
      <section id="academy" className="snap-section academy-section">
        <BackgroundType />
        <div className="section-inner" ref={academyRef}>
          <div className={`academy-box ${academyVisible ? 'revealed' : ''}`}>
            <div className="academy-info">
              <span className="tag reveal-item">03 / Training</span>
              <h2 className="reveal-item" style={{ transitionDelay: '0.1s' }}>
                AVTECH <em>Academy</em>
              </h2>
              <p className="reveal-item" style={{ transitionDelay: '0.2s' }}>
                We teach production-level software design. Build real-world full-stack
                architectures on MERN, Next.js, and PHP · Laravel, participate in
                interactive code reviews, and earn direct verification.
              </p>
              <ul className="academy-perks">
                {[
                  'Advanced MERN & Next.js Stacks',
                  'PHP · Laravel Backend Engineering',
                  'Flutter & React Native Mobile Apps',
                  'Local Database & Sync Mechanics',
                  'Real-World Systems Design',
                  
                ].map((perk, idx) => (
                  <li key={idx} className="reveal-item" style={{ transitionDelay: `${0.3 + idx * 0.1}s` }}>
                    <span className="perk-check">✓</span>
                    {perk}
                  </li>
                ))}
              </ul>
            </div>
            <div className="academy-visual">
              <div className="visual-stat reveal-item" style={{ transitionDelay: '0.3s' }}>
                <h3><AnimatedCounter value="12" suffix="-Wk" /></h3>
                <p>Cohort Duration</p>
              </div>
              <div className="visual-stat reveal-item" style={{ transitionDelay: '0.45s' }}>
                <h3>1-on-1</h3>
                <p>Mentor Reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          SECTION 5: CONTACT
         ============================== */}
      <section className="snap-section contact-section">
        <BackgroundType />
        <div className="section-inner" ref={contactRef}>
          <div className={`contact-main ${contactVisible ? 'revealed' : ''}`}>
            <span className="tag reveal-item">04 / Initiate</span>
            <h2 className="reveal-item" style={{ transitionDelay: '0.1s' }}>
              Ready to <em>work together?</em>
            </h2>
            <p className="reveal-item" style={{ transitionDelay: '0.2s' }}>
              Deploy a product, commission custom builds, or advance your engineering career.
            </p>
            <a href="mailto:avtechbmt@gmail.com" className="cta-primary reveal-item" style={{ transitionDelay: '0.3s' }}>
              <span>Contact Our Engineers</span>
              <svg className="btn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* ==============================
          SECTION 7: GALLERY
         ============================== */}
      <section id="gallery" className="snap-section gallery-section">
        <BackgroundType />

        <div className="section-inner" ref={galleryRef}>
          <div className={`section-header gallery-header-flex ${galleryVisible ? 'revealed' : ''}`}>
            <div>
              <span className="tag reveal-item">06 / Life & Workspace</span>
              <h2 className="reveal-item" style={{ transitionDelay: '0.1s' }}>
                Campus & <em>Training Moments</em>
              </h2>
            </div>
            <a href="/gallery" className="gallery-see-all-btn reveal-item" style={{ transitionDelay: '0.2s' }}>
              View All Photos ({galleryItems.length})
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>

          {loadingGallery ? (
            <div className="gallery-skeleton">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="skeleton-item" style={{ animationDelay: `${i * 0.1}s` }} />
              ))}
            </div>
          ) : visibleGalleryItems.length === 0 ? (
            <div className="gallery-empty">
              {galleryError || 'No photos published yet.'}
            </div>
          ) : (
            <div className="gallery-showcase-grid">
              {visibleGalleryItems.map((item, idx) => {
                const isOdd = idx % 2 !== 0;
                const fullImageUrl = getImageUrl(item.imageUrl);
                const isFailed = !!imageErrors[item._id];

                return (
                  <div
                    key={item._id || idx}
                    className={`gallery-card-item ${isOdd ? 'odd-item' : 'even-item'} ${isFailed ? 'failed-image' : ''}`}
                    style={revealStyle(0.15 + idx * 0.08)}
                    onClick={() => !isFailed && setSelectedImage(item)}
                  >
                    <div className="gallery-img-wrapper">
                      {!isFailed ? (
                        <img
                          src={fullImageUrl}
                          alt={item.title || 'Gallery Item'}
                          loading="lazy"
                          onError={() => handleImageError(item._id, fullImageUrl)}
                        />
                      ) : (
                        <div className="gallery-img-placeholder">
                          <span>Image Unavailable</span>
                        </div>
                      )}
                      {!isFailed && (
                        <div className="gallery-hover-overlay">
                          <span className="view-btn">View Photo ↗</span>
                        </div>
                      )}
                    </div>

                    <div className="gallery-item-meta">
                      <span className="gallery-index">0{idx + 1}</span>
                      <div className="gallery-title-box">
                        <h4>{item.title}</h4>
                        <span className="gallery-category-badge">{item.category}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* FULL VIEW LIGHTBOX */}
      {selectedImage && (
        <div className="gallery-modal-backdrop" onClick={() => setSelectedImage(null)}>
          <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="gallery-modal-close" onClick={() => setSelectedImage(null)} aria-label="Close modal">
              ✕
            </button>
            <div className="gallery-modal-img-container">
              <img
                src={getImageUrl(selectedImage.imageUrl)}
                alt={selectedImage.title || 'Selected View'}
                onError={(e) => {
                  e.currentTarget.style.opacity = '0.3';
                  e.currentTarget.alt = 'Image failed to load';
                }}
              />
            </div>
            <div className="gallery-modal-details">
              <span className="tag">{selectedImage.category}</span>
              <h3>{selectedImage.title}</h3>
              {selectedImage.description && <p>{selectedImage.description}</p>}
            </div>
          </div>
        </div>
      )}

      {/* SECTION 8: FOOTER */}
      <section className="snap-section footer-snap-section">
        <BackgroundType />
        <Footer />
      </section>
    </div>
  );
}